import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Document, Page, Thumbnail, pdfjs } from 'react-pdf';
import {
  LeftOutlined,
  RightOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
} from '@ant-design/icons';
import DrawingCanvas from '../VideoPlayer/DrawingCanvas';
import type { Shape, DrawingTool } from '../VideoPlayer/DrawingCanvas';
import styles from './PdfPlayer.module.scss';
import vpStyles from '../VideoPlayer/VideoPlayer.module.scss';
import { getUserDisplayNameWithDeleted, isUserDeleted } from '../../utils/userPresence';
import { DeletedUserAvatarShell } from '../UserPresence/DeletedUserAvatarShell';

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.mjs`;

/** Same zoom bounds idea as VideoPlayer image mode */
const PDF_ZOOM_MIN = 0.5;
const PDF_ZOOM_MAX = 4;
const PDF_ZOOM_STEP = 0.05;
const PDF_ZOOM_DEFAULT = 0.75;

/** pdf.js document from react-pdf Document onLoadSuccess (lightweight typing without pdfjs-dist root) */
interface PdfJsDocumentLike {
  numPages: number;
  getPage: (pageNumber: number) => Promise<{
    getViewport: (opts: { scale: number }) => { width: number; height: number };
  }>;
}

function clampPdfPan(
  pan: { x: number; y: number },
  dispW: number,
  dispH: number,
  vw: number,
  vh: number,
) {
  const maxX = Math.max(0, (dispW - vw) / 2);
  const maxY = Math.max(0, (dispH - vh) / 2);
  return {
    x: Math.min(maxX, Math.max(-maxX, pan.x)),
    y: Math.min(maxY, Math.max(-maxY, pan.y)),
  };
}

/** Row shape from getFormattedComments for thumbnail avatars */
export type PdfThumbnailComment = {
  id: number;
  startFrame: number;
  endFrame: number;
  versionId?: number;
  isResolved?: boolean;
  user?: {
    id?: number;
    firstName?: string;
    lastName?: string;
    email?: string;
    thumbnailUrl?: string | null;
    isDeleted?: boolean;
  } | null;
};

export interface PdfPlayerProps {
  fileUrl: string;
  title?: string;
  drawingToolbox?: React.ReactNode;
  isDrawingMode?: boolean;
  selectedCommentId?: number | null;
  shapes: Shape[];
  drawingTool: DrawingTool;
  drawingColor: string;
  selectedShapeId: string | null;
  onShapesChange: (shapes: Shape[]) => void;
  onTextToolClick: (pos: { x: number; y: number }) => void;
  onShapeSelection: (shapeId: string | null) => void;
  onDrawingComplete: () => void;
  /** Comments: page number = frame; parent syncs state */
  onPageChange?: (page: number) => void;
  /** Jump to page when selecting a comment (token disambiguates repeats) */
  pageJumpRequest?: { token: number; page: number } | null;
  /** User changed page via prev/next, thumbnail, or in-PDF link (not initial load / comment jump) */
  onPdfUserNavigate?: () => void;
  onDocumentInfo?: (info: { numPages: number }) => void;
  pdfComments?: PdfThumbnailComment[];
  pdfCommentVersionId?: number;
  /** When true, resolved comments also show on thumbnails (same as seek bar) */
  showResolvedPins?: boolean;
}

const PdfPlayer: React.FC<PdfPlayerProps> = ({
  fileUrl,
  title,
  drawingToolbox,
  isDrawingMode = false,
  selectedCommentId = null,
  shapes,
  drawingTool,
  drawingColor,
  selectedShapeId,
  onShapesChange,
  onTextToolClick,
  onShapeSelection,
  onDrawingComplete,
  onPageChange,
  pageJumpRequest = null,
  onPdfUserNavigate,
  onDocumentInfo,
  pdfComments = [],
  pdfCommentVersionId,
  showResolvedPins = false,
}) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const pageNumberRef = useRef(1);
  pageNumberRef.current = pageNumber;
  const lastJumpTokenRef = useRef(0);
  const [pdfDoc, setPdfDoc] = useState<PdfJsDocumentLike | null>(null);
  const [pageNatural, setPageNatural] = useState<{ w: number; h: number } | null>(null);
  const [viewportSize, setViewportSize] = useState({ w: 0, h: 0 });
  const [pdfZoom, setPdfZoom] = useState(PDF_ZOOM_DEFAULT);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const panRef = useRef(pan);
  useEffect(() => {
    panRef.current = pan;
  }, [pan]);
  const [panDragging, setPanDragging] = useState(false);
  const panDragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    origin: { x: number; y: number };
  } | null>(null);

  const [docError, setDocError] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const thumbnailStripRef = useRef<HTMLDivElement>(null);

  const isDrawingEnabled = isDrawingMode && selectedCommentId == null;
  const drawingToolActive = isDrawingEnabled && drawingTool !== 'none';
  const pdfPanEnabled = !drawingToolActive;

  /** Keep parent state/ref in sync on the same tick when the page changes (correct page on submit). */
  const setPageClamped = useCallback(
    (page: number) => {
      const cap =
        numPages != null && numPages > 0
          ? Math.min(Math.max(1, Math.round(page)), numPages)
          : Math.max(1, Math.round(page));
      pageNumberRef.current = cap;
      setPageNumber(cap);
      onPageChange?.(cap);
    },
    [numPages, onPageChange],
  );

  const setPageClampedFromUser = useCallback(
    (page: number) => {
      setPageClamped(page);
      onPdfUserNavigate?.();
    },
    [setPageClamped, onPdfUserNavigate],
  );

  useEffect(() => {
    lastJumpTokenRef.current = 0;
    pageNumberRef.current = 1;
    setPageNumber(1);
    onPageChange?.(1);
    setNumPages(null);
    setPdfDoc(null);
    setPageNatural(null);
    setDocError(null);
    setPan({ x: 0, y: 0 });
    setPdfZoom(PDF_ZOOM_DEFAULT);
  }, [fileUrl, onPageChange]);

  useEffect(() => {
    setPan({ x: 0, y: 0 });
  }, [pageNumber]);

  useEffect(() => {
    if (!pageJumpRequest) return;
    if (pageJumpRequest.token === lastJumpTokenRef.current) return;
    lastJumpTokenRef.current = pageJumpRequest.token;
    const raw = pageJumpRequest.page;
    const cap =
      numPages != null ? Math.min(Math.max(1, raw), numPages) : Math.max(1, raw);
    setPageClamped(cap);
  }, [pageJumpRequest, numPages, setPageClamped]);

  useEffect(() => {
    if (!pdfDoc || pageNumber < 1) return;
    let cancelled = false;
    pdfDoc
      .getPage(pageNumber)
      .then((p) => {
        const vp = p.getViewport({ scale: 1 });
        if (!cancelled) setPageNatural({ w: vp.width, h: vp.height });
      })
      .catch(() => {
        if (!cancelled) setPageNatural(null);
      });
    return () => {
      cancelled = true;
    };
  }, [pdfDoc, pageNumber]);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const read = () => {
      setViewportSize({ w: el.clientWidth, h: el.clientHeight });
    };
    const ro = new ResizeObserver(() => read());
    ro.observe(el);
    read();
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(read);
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      ro.disconnect();
    };
  }, [docError, numPages]);

  /** In flex, clientWidth/Height can stay 0; no transform and stuck on “Preparing page…” */
  const effectiveViewport = useMemo(() => {
    const w = viewportSize.w;
    const h = viewportSize.h;
    if (typeof window === 'undefined') {
      return { w: Math.max(w, 800), h: Math.max(h, 600) };
    }
    const fw = Math.min(1400, Math.max(320, window.innerWidth - 120));
    const fh = Math.min(900, Math.max(280, window.innerHeight - 260));
    return {
      w: w >= 2 ? w : fw,
      h: h >= 2 ? h : fh,
    };
  }, [viewportSize.w, viewportSize.h]);

  const pdfTransform = useMemo(() => {
    if (!pageNatural) return null;
    const vw = effectiveViewport.w;
    const vh = effectiveViewport.h;
    const nw = pageNatural.w;
    const nh = pageNatural.h;
    if (nw < 1 || nh < 1) return null;
    const fit = Math.min(vw / nw, vh / nh);
    const baseW = Math.max(1, Math.round(nw * fit));
    const baseH = Math.max(1, Math.round(nh * fit));
    const z = pdfZoom;
    let visualScale = z;
    if (z > 1 && baseW * z < vw - 0.5) {
      visualScale = (vw * z) / baseW;
    }
    return {
      baseW,
      baseH,
      visualScale,
      dispW: baseW * visualScale,
      dispH: baseH * visualScale,
    };
  }, [pageNatural, effectiveViewport.w, effectiveViewport.h, pdfZoom]);

  const pdfStageSize = useMemo(() => {
    if (!pdfTransform) return { width: 0, height: 0 };
    return { width: pdfTransform.baseW, height: pdfTransform.baseH };
  }, [pdfTransform]);

  useEffect(() => {
    if (!pdfTransform) return;
    const { dispW, dispH } = pdfTransform;
    const { w: vw, h: vh } = effectiveViewport;
    setPan((p) => clampPdfPan(p, dispW, dispH, vw, vh));
  }, [pdfTransform, effectiveViewport.w, effectiveViewport.h, pdfZoom]);

  const handlePanPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!pdfPanEnabled) return;
      if (e.button !== 0) return;
      e.preventDefault();
      e.currentTarget.setPointerCapture(e.pointerId);
      panDragRef.current = {
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        origin: { ...panRef.current },
      };
      setPanDragging(true);
    },
    [pdfPanEnabled],
  );

  const handlePanPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const d = panDragRef.current;
      if (!d || d.pointerId !== e.pointerId || !pdfTransform) return;
      e.preventDefault();
      const dx = e.clientX - d.startX;
      const dy = e.clientY - d.startY;
      const { w: vw, h: vh } = effectiveViewport;
      const { dispW, dispH } = pdfTransform;
      setPan(
        clampPdfPan(
          { x: d.origin.x + dx, y: d.origin.y + dy },
          dispW,
          dispH,
          vw,
          vh,
        ),
      );
    },
    [pdfTransform, effectiveViewport.w, effectiveViewport.h],
  );

  const handlePanPointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const d = panDragRef.current;
    if (d && d.pointerId === e.pointerId) {
      panDragRef.current = null;
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      setPanDragging(false);
    }
  }, []);

  const onDocumentLoadSuccess = useCallback(
    (doc: PdfJsDocumentLike) => {
      setPdfDoc(doc);
      setNumPages(doc.numPages);
      setDocError(null);
      const first = 1;
      pageNumberRef.current = first;
      setPageNumber(first);
      onPageChange?.(first);
      onDocumentInfo?.({ numPages: doc.numPages });
    },
    [onDocumentInfo, onPageChange],
  );

  const onDocumentLoadError = useCallback((err: unknown) => {
    const msg =
      err instanceof Error ? err.message : typeof err === 'string' ? err : 'Failed to load PDF';
    setDocError(msg);
    setNumPages(null);
    setPdfDoc(null);
  }, []);

  /** In-PDF links (e.g. jump to page 6); without this react-pdf warns and the page may not change */
  const handleDocumentItemClick = useCallback(
    (args: { pageNumber: number; pageIndex?: number }) => {
      let next = args.pageNumber;
      if (!Number.isFinite(next) || next < 1) {
        const idx = args.pageIndex;
        if (typeof idx === 'number' && idx >= 0) next = idx + 1;
      }
      if (!Number.isFinite(next) || next < 1) return;
      if (numPages != null && next > numPages) return;
      setPageClampedFromUser(next);
    },
    [numPages, setPageClampedFromUser],
  );

  useLayoutEffect(() => {
    const strip = thumbnailStripRef.current;
    if (!strip) return;
    const active = strip.querySelector(`[data-thumb-page="${pageNumber}"]`);
    active?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [pageNumber]);

  const goPrev = useCallback(() => {
    setPageClampedFromUser(Math.max(1, pageNumberRef.current - 1));
  }, [setPageClampedFromUser]);

  const goNext = useCallback(() => {
    if (!numPages) return;
    setPageClampedFromUser(Math.min(numPages, pageNumberRef.current + 1));
  }, [numPages, setPageClampedFromUser]);

  const zoomIn = useCallback(() => {
    setPdfZoom((z) => Math.min(PDF_ZOOM_MAX, z + PDF_ZOOM_STEP));
  }, []);

  const zoomOut = useCallback(() => {
    setPdfZoom((z) => Math.max(PDF_ZOOM_MIN, z - PDF_ZOOM_STEP));
  }, []);

  const handlePdfWheelZoom = useCallback((e: React.WheelEvent<HTMLDivElement>) => {
    e.preventDefault();
    const direction = e.deltaY < 0 ? 1 : -1;
    setPdfZoom((z) =>
      Math.max(PDF_ZOOM_MIN, Math.min(PDF_ZOOM_MAX, z + direction * PDF_ZOOM_STEP)),
    );
  }, []);

  const toggleFullscreen = useCallback(() => {
    const el = rootRef.current;
    if (!el) return;
    const doc = document as Document & {
      webkitFullscreenElement?: Element | null;
      webkitExitFullscreen?: () => Promise<void>;
    };
    const fsEl =
      document.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
    if (fsEl === el) {
      if (document.exitFullscreen) {
        void document.exitFullscreen();
      } else if (doc.webkitExitFullscreen) {
        void doc.webkitExitFullscreen();
      }
      return;
    }
    if (el.requestFullscreen) {
      void el.requestFullscreen();
    } else if (
      (el as unknown as HTMLElement & { webkitRequestFullscreen?: () => void }).webkitRequestFullscreen
    ) {
      void (el as unknown as HTMLElement & { webkitRequestFullscreen: () => void }).webkitRequestFullscreen();
    }
  }, []);

  const thumbList = useMemo(() => {
    if (!numPages) return null;
    return Array.from({ length: numPages }, (_, i) => i + 1);
  }, [numPages]);

  const commentAuthorsByPage = useMemo(() => {
    const map = new Map<number, NonNullable<PdfThumbnailComment['user']>[]>();
    const maxP = numPages != null && numPages > 0 ? numPages : 999999;
    if (pdfCommentVersionId == null || !pdfComments?.length) return map;

    let visible = pdfComments.filter((c) => c.versionId === pdfCommentVersionId);
    if (!showResolvedPins) {
      visible = visible.filter((c) => !c.isResolved);
    }
    visible = [...visible].sort((a, b) => a.id - b.id);

    const userDedupeKey = (u: NonNullable<PdfThumbnailComment['user']>): string =>
      String(u.id ?? u.email ?? '');

    for (const c of visible) {
      const u = c.user;
      if (!u) continue;
      const uk = userDedupeKey(u);
      if (uk === '') continue;

      const start = Math.max(1, Math.min(maxP, Math.floor(Number(c.startFrame) || 1)));
      const end = Math.max(1, Math.min(maxP, Math.floor(Number(c.endFrame) || start)));
      const lo = Math.min(start, end);
      const hi = Math.max(start, end);

      for (let p = lo; p <= hi; p += 1) {
        let row = map.get(p);
        if (!row) {
          row = [];
          map.set(p, row);
        }
        if (!row.some((ex) => userDedupeKey(ex) === uk)) {
          row.push(u);
        }
      }
    }
    return map;
  }, [pdfComments, pdfCommentVersionId, showResolvedPins, numPages]);

  const zoomPercentLabel = Math.round(pdfZoom * 100);

  if (!fileUrl) {
    return (
      <div className={styles.root}>
        <div className={styles.errorBox}>PDF address not found.</div>
      </div>
    );
  }

  return (
    <div className={styles.root} ref={rootRef} aria-label={title}>
      <Document
        className={styles.documentRoot}
        file={fileUrl}
        onLoadSuccess={onDocumentLoadSuccess}
        onLoadError={onDocumentLoadError}
        onItemClick={handleDocumentItemClick}
        loading={<div className={styles.loadingBox}>Loading PDF…</div>}
        error={
          <div className={styles.errorBox}>
            {docError || 'Could not open PDF.'}
          </div>
        }
      >
        {!docError ? (
          <div className={styles.docColumn}>
            <div
              ref={viewportRef}
              className={styles.pdfViewport}
              style={{
                cursor: pdfPanEnabled
                  ? panDragging
                    ? 'grabbing'
                    : 'grab'
                  : undefined,
              }}
              onPointerDown={handlePanPointerDown}
              onPointerMove={handlePanPointerMove}
              onPointerUp={handlePanPointerUp}
              onPointerCancel={handlePanPointerUp}
              onWheel={handlePdfWheelZoom}
            >
              {pdfTransform ? (
                <div
                  className={styles.panOuter}
                  style={{
                    transform: `translate(${pan.x}px, ${pan.y}px)`,
                  }}
                >
                  <div
                    className={styles.panInner}
                    style={{
                      width: pdfTransform.baseW,
                      height: pdfTransform.baseH,
                      transform: `scale(${pdfTransform.visualScale})`,
                    }}
                  >
                    <div className={styles.mainPageBox}>
                      <Page
                        key={`${fileUrl}-${pageNumber}`}
                        pageNumber={pageNumber}
                        width={pdfTransform.baseW}
                        renderTextLayer
                        renderAnnotationLayer
                      />
                      {pdfStageSize.width > 0 && pdfStageSize.height > 0 ? (
                        <div
                          className={styles.drawingOverlay}
                          style={{
                            width: pdfStageSize.width,
                            height: pdfStageSize.height,
                            pointerEvents: isDrawingEnabled ? 'auto' : 'none',
                          }}
                        >
                          <DrawingCanvas
                            stageSize={pdfStageSize}
                            shapes={shapes}
                            tool={drawingTool}
                            color={drawingColor}
                            selectedShapeId={selectedShapeId}
                            isDrawingEnabled={isDrawingEnabled}
                            onShapesChange={onShapesChange}
                            onTextToolClick={onTextToolClick}
                            onShapeSelection={onShapeSelection}
                            onDrawingComplete={onDrawingComplete}
                          />
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              ) : (
                <div className={styles.viewportPlaceholder}>Preparing page…</div>
              )}
            </div>

            {thumbList && thumbList.length > 0 ? (
              <div ref={thumbnailStripRef} className={styles.thumbnailStrip}>
                {thumbList.map((n) => (
                  <div
                    key={n}
                    data-thumb-page={n}
                    role="button"
                    tabIndex={0}
                    className={`${styles.thumbnailBtn} ${n === pageNumber ? styles.thumbnailBtnActive : ''}`}
                    onClick={() => setPageClampedFromUser(n)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setPageClampedFromUser(n);
                      }
                    }}
                    aria-label={`Page ${n}`}
                  >
                    <Thumbnail pageNumber={n} width={80} />
                    {(() => {
                      const authors = commentAuthorsByPage.get(n) ?? [];
                      if (authors.length === 0) return null;
                      const shown = authors.slice(0, 3);
                      const more = authors.length - 3;
                      return (
                        <div className={styles.thumbCommentAvatars} aria-hidden>
                          {shown.map((u, idx) => (
                            <div
                              key={`${n}-${u.id ?? u.email ?? idx}`}
                              className={styles.thumbCommentAvatar}
                              style={{
                                marginLeft: idx > 0 ? -4 : 0,
                                zIndex: 3 - idx,
                              }}
                              title={getUserDisplayNameWithDeleted(u, null)}
                            >
                              <DeletedUserAvatarShell deleted={isUserDeleted(u)}>
                                {u.thumbnailUrl ? (
                                  <img src={u.thumbnailUrl} alt="" />
                                ) : (
                                  <span className={styles.thumbCommentAvatarLetter}>
                                    {(u.firstName?.[0] ?? u.email?.[0] ?? 'U').toUpperCase()}
                                  </span>
                                )}
                              </DeletedUserAvatarShell>
                            </div>
                          ))}
                          {more > 0 ? (
                            <div
                              className={styles.thumbCommentAvatarMore}
                              style={{ marginLeft: -4, zIndex: 4 }}
                            >
                              +{more}
                            </div>
                          ) : null}
                        </div>
                      );
                    })()}
                  </div>
                ))}
              </div>
            ) : null}

            <div className={styles.floatingBar}>
              <div className={styles.pdfBarPrimary}>
                <div className={styles.pageIndicatorGroup}>
                  {numPages ? (
                    <>
                      <span className={styles.pageCurrentChip}>
                        {String(pageNumber).padStart(2, '0')}
                      </span>
                      <span className={styles.pageIndicatorRest}>
                        {' '}
                        / {String(numPages).padStart(2, '0')}
                      </span>
                    </>
                  ) : (
                    <span className={styles.pageIndicatorRest}>—</span>
                  )}
                </div>
                <div className={styles.navArrows}>
                  <button
                    type="button"
                    className={styles.iconBtn}
                    onClick={goPrev}
                    disabled={!numPages || pageNumber <= 1}
                    aria-label="Previous page"
                  >
                    <LeftOutlined />
                  </button>
                  <button
                    type="button"
                    className={styles.iconBtn}
                    onClick={goNext}
                    disabled={!numPages || pageNumber >= numPages}
                    aria-label="Next page"
                  >
                    <RightOutlined />
                  </button>
                </div>
                <div className={styles.zoomGroup}>
                  <button
                    type="button"
                    className={styles.iconBtn}
                    onClick={zoomOut}
                    disabled={pdfZoom <= PDF_ZOOM_MIN}
                    aria-label="Zoom out"
                  >
                    <ZoomOutOutlined />
                  </button>
                  <button
                    type="button"
                    className={styles.zoomResetBtn}
                    onClick={() => setPdfZoom(1)}
                    aria-label="Zoom %100"
                    title="Zoom %100"
                  >
                    <span className={styles.zoomLabel}>{zoomPercentLabel}%</span>
                  </button>
                  <button
                    type="button"
                    className={styles.iconBtn}
                    onClick={zoomIn}
                    disabled={pdfZoom >= PDF_ZOOM_MAX}
                    aria-label="Zoom in"
                  >
                    <ZoomInOutlined />
                  </button>
                </div>
              </div>

              <div className={styles.floatingBarRight}>
                {drawingToolbox ? (
                  <div
                    className={`${vpStyles.bottomControlsToolbar} ${styles.pdfFloatingToolbar}`}
                  >
                    <div className={styles.toolboxAnchor}>{drawingToolbox}</div>
                  </div>
                ) : null}
                <div className={styles.pdfFloatingExpandSlot}>
                  <button
                    type="button"
                    className={`${vpStyles.iconControlBtn} ${vpStyles.iconControlBtnMedia}`}
                    onClick={toggleFullscreen}
                    aria-label="Full screen"
                  >
                    <img
                      src="/images/expand.svg"
                      alt=""
                      className={vpStyles.controlGlyphImg}
                      width={20}
                      height={20}
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </Document>
    </div>
  );
};

export default PdfPlayer;
